import React, { ReactElement, useState } from 'react';
import cx from 'classnames';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ChevronDown, KeyRound, Mail, Trash2 } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/ui/tooltip';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { ClientAccessRole, ClientCompanyPerson, ClientCompanyProduct } from '@data';
import { reasonOf } from '@data/utils/errors';

const GROUPS: { role: ClientAccessRole; titleKey: string }[] = [
  { role: ClientAccessRole.admin, titleKey: 'access.admins' },
  { role: ClientAccessRole.read, titleKey: 'access.viewOnly' },
];

const displayName = (person: ClientCompanyPerson): string =>
  `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() || person.email;

// appearance-none with an overlaid chevron, so the arrow sits the same distance from the corner as the
// multi-select's own (px-3) rather than at the browser's default inset.
const selectBase =
  'h-8 appearance-none rounded-lg border pl-2.5 pr-8 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50';

const RoleSelect = ({
  value,
  onChange,
  ariaLabel,
  disabled,
}: {
  value: ClientAccessRole;
  onChange: (value: ClientAccessRole) => void;
  ariaLabel: string;
  disabled?: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <div className="relative shrink-0">
      <select
        aria-label={ariaLabel}
        value={value}
        disabled={disabled}
        onChange={e => onChange(e.target.value as ClientAccessRole)}
        className={cx(selectBase, 'w-full border-input bg-popover text-foreground disabled:opacity-60')}
      >
        <option value={ClientAccessRole.admin}>{t('access.admin')}</option>
        <option value={ClientAccessRole.read}>{t('access.viewOnly')}</option>
      </select>
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
      />
    </div>
  );
};

/**
 * Who at one client can read their reports, in the two groups the client's own panel shows: the admins
 * who can answer a report and manage access, then everyone who can only read.
 *
 * A super admin is the client's own top account, so their row reports what they may see rather than
 * offering to change it — the same protection the API applies.
 */
const AccessList = ({
  people,
  products,
  canEdit,
  onChange,
  onRemove,
  onSendSetPassword,
}: {
  people: ClientCompanyPerson[];
  products: ClientCompanyProduct[];
  canEdit: boolean;
  onChange: (values: { membershipId: string; role?: ClientAccessRole; productIds?: string[] }) => Promise<unknown>;
  onRemove: (membershipId: string) => Promise<unknown>;
  onSendSetPassword: (membershipId: string) => Promise<unknown>;
}): ReactElement => {
  const { t } = useTranslation();
  const [confirmRemove, setConfirmRemove] = useState<ClientCompanyPerson | null>(null);
  // Asked first, because this leaves the building: an email to a client cannot be taken back, and an
  // icon on its own cannot say that is what the click does.
  const [confirmSend, setConfirmSend] = useState<ClientCompanyPerson | null>(null);
  const [sending, setSending] = useState(false);
  // Whose row is mid-save, so a second tick cannot be made before the first has landed: each tick writes
  // the whole list, so two at once are two different lists and the later one loses the earlier one's tick.
  const [saving, setSaving] = useState<string | null>(null);

  const options = products.map(product => ({ value: product.id, label: product.name }));

  const save = async (values: {
    membershipId: string;
    role?: ClientAccessRole;
    productIds?: string[];
  }): Promise<void> => {
    setSaving(values.membershipId);
    try {
      await onChange(values);
    } catch (error) {
      // The API refuses two changes on purpose — demoting the last admin, and a product that is not this
      // client's — and both are worth saying out loud rather than leaving the row looking saved.
      const reason = reasonOf(error);
      toast.error(reason === 'lastClientAdmin' ? t('access.lastAdmin') : t('access.changeFailed'));
    } finally {
      setSaving(null);
    }
  };

  /**
   * A client account is created without a password, so this is how the person is first let in. Offered
   * on every row an admin can see, the super admin's included: they need to sign in as much as anyone,
   * and their row is protected against changes rather than against being helped.
   */
  const sendSetPassword = async (person: ClientCompanyPerson): Promise<void> => {
    setSending(true);
    try {
      await onSendSetPassword(person.membershipId);
      toast.success(t('access.setPasswordSent', { email: person.email }));
      setConfirmSend(null);
    } catch (error) {
      // Both refusals are the environment's doing rather than the admin's, so each says which it was.
      // The dialog stays open on a failure, so the refusal is read beside the person it was about.
      const reason = reasonOf(error);
      if (reason === 'emailDisabled') toast.error(t('access.setPasswordEmailOff'));
      else if (reason === 'userDeactivated') toast.error(t('access.setPasswordDeactivated'));
      else toast.error(t('access.setPasswordFailed'));
    } finally {
      setSending(false);
    }
  };

  /** The icon says what it does on hover; the dialog behind it says it before anything is sent. */
  const sendButton = (person: ClientCompanyPerson): ReactElement => (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setConfirmSend(person)}
          aria-label={t('access.sendSetPasswordTo', { email: person.email })}
          className="shrink-0 text-muted-foreground hover:text-foreground"
        >
          <KeyRound className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{t('access.sendSetPasswordHint')}</TooltipContent>
    </Tooltip>
  );

  /** What a row says when it cannot be edited: the scope, short, the way the clients table says it. */
  const scope = (person: ClientCompanyPerson): ReactElement => {
    if (person.allProducts) return <span className="text-xs text-muted-foreground">{t('allProducts')}</span>;
    if (!person.products.length) return <span className="text-xs text-muted-foreground">{t('noProducts')}</span>;
    const [first, ...rest] = person.products;
    if (!rest.length) return <span className="text-xs text-muted-foreground">{first}</span>;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default text-xs text-muted-foreground">
            {first} <span className="font-bold text-foreground">{t('nMore', { count: rest.length })}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{person.products.join(', ')}</TooltipContent>
      </Tooltip>
    );
  };

  const row = (person: ClientCompanyPerson): ReactElement => {
    const name = displayName(person);
    const editable = canEdit && !person.isSuperAdmin;
    return (
      <div key={person.membershipId} className="flex items-center gap-3 border-b border-border py-2.5 last:border-b-0">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Mail className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{name}</p>
          {name !== person.email && <p className="truncate text-xs text-muted-foreground">{person.email}</p>}
        </div>
        {editable ? (
          <div className="flex items-center gap-3">
            <MultiSelectField
              label={<span className="sr-only">{t('a11y.productsPersonCanView', { email: person.email })}</span>}
              options={options}
              value={person.productIds}
              // Every tick writes the whole list, and the master row writes all of it or none of it.
              // What a product added later does is not this screen's business any more: it arrives
              // ticked, and this is where somebody unticks it if it was not theirs to read.
              onChange={productIds => save({ membershipId: person.membershipId, productIds })}
              allSelected={person.allProducts}
              onToggleAll={all =>
                save({ membershipId: person.membershipId, productIds: all ? options.map(option => option.value) : [] })
              }
              emptyLabel={t('noProducts')}
              summaryThreshold={0}
              renderCountSummary={count => (person.allProducts ? t('allProducts') : t('access.nProducts', { count }))}
              selectAllLabel={t('allProducts')}
              disabled={saving === person.membershipId}
              className="w-36 shrink-0 gap-0"
            />
            <RoleSelect
              ariaLabel={t('a11y.roleFor', { email: person.email })}
              value={person.role}
              onChange={role => save({ membershipId: person.membershipId, role })}
            />
            {sendButton(person)}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setConfirmRemove(person)}
              aria-label={`${t('actions.remove')} ${person.email}`}
              className="shrink-0 text-muted-foreground hover:text-foreground"
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <span className="shrink-0 text-right">{scope(person)}</span>
            {person.isSuperAdmin && (
              <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                {t('access.superAdmin')}
              </span>
            )}
            {/* The super admin's row refuses changes, not help: they still have to get in the first time. */}
            {canEdit && sendButton(person)}
          </div>
        )}
      </div>
    );
  };

  if (!people.length) return <p className="text-sm text-muted-foreground">{t('noPeopleWithAccess')}</p>;

  return (
    <>
      <div className="space-y-4">
        {GROUPS.map(group => {
          const inGroup = people.filter(person => person.role === group.role);
          if (!inGroup.length) return null;
          return (
            <section key={group.role}>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t(group.titleKey)}
              </p>
              <div>{inGroup.map(row)}</div>
            </section>
          );
        })}
      </div>

      <Dialog open={confirmSend !== null} onOpenChange={next => !next && !sending && setConfirmSend(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('access.confirmSetPasswordTitle', { name: confirmSend ? displayName(confirmSend) : '' })}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground">
            {t('access.confirmSetPasswordDescription', { email: confirmSend?.email ?? '' })}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={sending} onClick={() => setConfirmSend(null)}>
              {t('actions.cancel')}
            </Button>
            <Button type="button" disabled={sending} onClick={() => confirmSend && sendSetPassword(confirmSend)}>
              {t('access.sendSetPassword')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmRemove !== null} onOpenChange={next => !next && setConfirmRemove(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('confirmRemoveTitle', { name: confirmRemove ? displayName(confirmRemove) : '' })}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground">{t('removeAccessDescription')}</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmRemove(null)}>
              {t('actions.cancel')}
            </Button>
            <Button
              type="button"
              onClick={async () => {
                if (confirmRemove) {
                  await onRemove(confirmRemove.membershipId);
                  toast.success(t('access.personRemoved'));
                }
                setConfirmRemove(null);
              }}
            >
              {t('actions.remove')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AccessList;
